(() => {
  if (customElements.get('de-product-add-ons')) return;

  class DeProductAddOns extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.formId = this.dataset.productFormId;
      this.form = document.getElementById(this.formId);

      this.cards = Array.from(
        this.querySelectorAll('[data-de-addon-card]')
      );

      this.list =
        this.querySelector('[data-de-addon-list]');

      this.moreButton =
        this.querySelector('[data-de-addon-more]');

      this.moreLabel =
        this.querySelector('[data-de-addon-more-label]');

      this.visibleLimit = Math.max(
        1,
        Number(this.dataset.visibleLimit || 3)
      );

      this.productsLimit = Math.min(
        5,
        Math.max(1, Number(this.dataset.productsLimit || 5))
      );

      this.solarInputMax = Math.max(
        0,
        Number(this.dataset.solarInputMax || 0)
      );

      this.compatibilityNote = this.querySelector(
        '[data-de-addon-compatibility-note]'
      );

      this.expanded = false;

      this.mainVariants = this.readJson(
        '[data-de-addon-main-variants]'
      );

      this.currency =
        this.dataset.currency ||
        window.Shopify?.currency?.active ||
        'GBP';

      this.locale =
        this.dataset.locale ||
        document.documentElement.lang ||
        'en-GB';

      this.isSubmitting = false;

      if (!this.form || !this.cards.length) return;

      this.mainVariantInput =
        this.form.querySelector('[name="id"]');

      this.mainQuantityInput =
        this.form.querySelector('[name="quantity"]');

      this.submitButton =
        this.form.querySelector(
          '.js-product-form-submit-btn, [type="submit"][name="add"]'
        );

      this.dynamicCheckoutElements = Array.from(
        this.form.querySelectorAll(
          'sht-load-dyn-co-btn, .shopify-payment-button'
        )
      );

      this.dynamicCheckoutWrapper =
        this.form.querySelector('sht-load-dyn-co-btn') ||
        this.form.querySelector('.shopify-payment-button');

      this.errorBox =
        this.querySelector('[data-de-addon-error]');

      this.purchaseNote =
        this.querySelector('[data-de-addon-purchase-note]');

      this.applyRecommendationRules();
      this.bindMoreToggle();
      this.createBundleCheckoutButton();
      this.bindAddonCards();

      this.mainVariantInput?.addEventListener(
        'change',
        () => {
          this.expanded = false;
          this.applyRecommendationRules();
          this.updateTotals();
          this.updateCheckoutButton();
        }
      );

      this.mainQuantityInput?.addEventListener(
        'change',
        () => this.updateTotals()
      );

      this.mainQuantityInput?.addEventListener(
        'input',
        () => this.updateTotals()
      );

      this.form.addEventListener(
        'submit',
        (event) => this.onFormSubmit(event),
        true
      );

      this.updateTotals();
      this.updateCheckoutButton();
    }

    bindMoreToggle() {
      this.moreButton?.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.updateCardVisibility();
      });
    }

    getMainProductContext() {
      const variant = this.getCurrentMainVariant();
      const options = Array.isArray(variant?.options)
        ? variant.options.join(' ')
        : '';

      return [
        this.dataset.productContext || '',
        variant?.title || '',
        variant?.sku || '',
        options
      ]
        .join(' ')
        .toUpperCase();
    }

    isSolarBundle(context) {
      return (
        context.includes('SOLAR') ||
        context.includes('PANEL') ||
        /(?:SE|SP|SF)\s*-?\s*\d{3}/.test(context) ||
        /\+\s*\d{2,4}\s*W/.test(context)
      );
    }

    isAlreadyIncluded(card, context, isBundle) {
      const token = (card.dataset.addonToken || '')
        .toUpperCase();

      const kind = card.dataset.addonKind || 'accessory';

      if (!token) return false;

      if (token === 'PV-XT60') {
        return isBundle;
      }

      if (token === 'CAR-XT60') {
        return false;
      }

      if (kind === 'panel') {
        return false;
      }

      return context.includes(token);
    }

    getIncludedSolarWatts(context) {
      const tokenWatts = {
        SE100: 100,
        SP012: 100,
        SP027: 100,
        SF100: 100,
        SP029: 140,
        SE200: 200,
        SP033: 200,
        SP035: 200,
        SF200: 200,
        SP037: 400,
        SF400: 400,
        SP039: 600
      };

      let tokenTotal = 0;

      Object.entries(tokenWatts).forEach(([token, watts]) => {
        const before = context.match(
          new RegExp(`(\\d+)\\s*[X×]\\s*${token}`, 'i')
        );

        const after = context.match(
          new RegExp(`${token}\\s*[X×]\\s*(\\d+)`, 'i')
        );

        if (before) {
          tokenTotal += Number(before[1]) * watts;
        } else if (after) {
          tokenTotal += Number(after[1]) * watts;
        } else if (context.includes(token)) {
          tokenTotal += watts;
        }
      });

      const genericTotals = [];
      const quantityPattern = /(\d+)\s*[X×]\s*(\d{2,4})\s*W/gi;
      const linkedPattern = /(?:\+|MIT)\s*(\d{2,4})\s*W/gi;
      const panelPattern = /(\d{2,4})\s*W[-\s]*(?:SOLAR)?PANEL/gi;

      let match;
      while ((match = quantityPattern.exec(context))) {
        genericTotals.push(Number(match[1]) * Number(match[2]));
      }

      while ((match = linkedPattern.exec(context))) {
        genericTotals.push(Number(match[1]));
      }

      while ((match = panelPattern.exec(context))) {
        genericTotals.push(Number(match[1]));
      }

      return Math.max(tokenTotal, ...genericTotals, 0);
    }

    isPanelWithinSolarLimit(card, context, isBundle) {
      if (!isBundle || card.dataset.addonKind !== 'panel') {
        return true;
      }

      const panelWatts = Number(card.dataset.addonWatts || 0);
      const includedWatts = this.getIncludedSolarWatts(context);

      if (!this.solarInputMax || !panelWatts || !includedWatts) {
        return false;
      }

      return (
        includedWatts + panelWatts <= this.solarInputMax
      );
    }

    updateTypeLabel(card, isBundle) {
      const label = card.querySelector(
        '[data-de-addon-type-label]'
      );

      if (!label) return;

      const kind = card.dataset.addonKind || 'accessory';
      const token = card.dataset.addonToken || '';

      if (kind === 'panel') {
        label.textContent = isBundle
          ? 'Additional Solar Panel'
          : 'Compatible Solar Panel';
      } else if (kind === 'battery') {
        label.textContent = 'Expand Your Capacity';
      } else if (kind === 'station') {
        label.textContent = token === 'R1500-LITE'
          ? 'Higher-capacity Option'
          : 'Portable Companion';
      } else if (token === 'PV-XT60') {
        label.textContent = 'Solar Connection';
      } else {
        label.textContent = 'Useful Accessory';
      }
    }

    getRecommendationPriority(card, isBundle) {
      const kind = card.dataset.addonKind || 'accessory';
      const token = card.dataset.addonToken || '';

      if (isBundle) {
        if (token === 'CAR-XT60') return 0;
        if (kind === 'battery') return 1;
        if (kind === 'panel') return 2;
        if (kind === 'accessory') return 3;
        if (kind === 'station') return 4;
        return 4;
      }

      if (kind === 'battery') return 0;
      if (kind === 'panel') return 1;
      if (token === 'PV-XT60') return 2;
      if (token === 'CAR-XT60') return 3;
      if (kind === 'station') return 4;
      return 4;
    }

    applyRecommendationRules() {
      if (!this.list) return;

      const context = this.getMainProductContext();
      const isBundle = this.isSolarBundle(context);

      this.cards.forEach((card) => {
        const included = this.isAlreadyIncluded(
          card,
          context,
          isBundle
        );

        const stationOnSingle =
          !isBundle && card.dataset.addonKind === 'station';

        const panelOutsideLimit = !this.isPanelWithinSolarLimit(
          card,
          context,
          isBundle
        );

        const hidden =
          included || stationOnSingle || panelOutsideLimit;

        card.dataset.ruleHidden = String(hidden);
        this.updateTypeLabel(card, isBundle);

        if (hidden) {
          const checkbox = card.querySelector(
            '[data-de-addon-checkbox]'
          );

          if (checkbox) checkbox.checked = false;
          card.classList.remove('is-selected');
          this.setQuantityEnabled(card, false);
        }
      });

      const orderedCards = this.cards
        .slice()
        .sort((first, second) => {
          const priorityDifference =
            this.getRecommendationPriority(first, isBundle) -
            this.getRecommendationPriority(second, isBundle);

          if (priorityDifference) return priorityDifference;

          return (
            Number(first.dataset.ruleIndex || 0) -
            Number(second.dataset.ruleIndex || 0)
          );
        });

      let allowedCount = 0;

      orderedCards.forEach((card) => {
        if (card.dataset.ruleHidden !== 'true') {
          allowedCount += 1;

          if (allowedCount > this.productsLimit) {
            card.dataset.ruleHidden = 'true';
          }
        }

        this.list.appendChild(card);
      });

      if (this.compatibilityNote) {
        const hasAdditionalPanel = orderedCards.some(
          (card) =>
            card.dataset.ruleHidden !== 'true' &&
            card.dataset.addonKind === 'panel'
        );

        this.compatibilityNote.hidden = !(
          isBundle && hasAdditionalPanel
        );
      }

      this.updateCardVisibility();
    }

    getRuleVisibleCards() {
      return this.cards.filter(
        (card) => card.dataset.ruleHidden !== 'true'
      );
    }

    updateCardVisibility() {
      const visibleCards = this.getRuleVisibleCards();

      visibleCards.forEach((card, index) => {
        const collapsed =
          !this.expanded && index >= this.visibleLimit;

        card.hidden = collapsed;
        card.classList.toggle('is-collapsed', collapsed);
      });

      this.cards
        .filter((card) => card.dataset.ruleHidden === 'true')
        .forEach((card) => {
          card.hidden = true;
          card.classList.add('is-rule-hidden');
        });

      if (!this.moreButton) return;

      const remaining = Math.max(
        0,
        visibleCards.length - this.visibleLimit
      );

      this.moreButton.hidden = remaining === 0;
      this.moreButton.setAttribute(
        'aria-expanded',
        String(this.expanded)
      );
      this.moreButton.classList.toggle(
        'is-expanded',
        this.expanded
      );

      if (!this.moreLabel) return;

      if (this.expanded) {
        this.moreLabel.textContent = 'Show less';
      } else if (remaining === 1) {
        this.moreLabel.textContent = 'Show 1 more product';
      } else {
        this.moreLabel.textContent =
          `Show ${remaining} more products`;
      }
    }

    bindAddonCards() {
      this.cards.forEach((card) => {
        const checkbox =
          card.querySelector('[data-de-addon-checkbox]');

        const select =
          card.querySelector('[data-de-addon-variant-select]');

        const quantityInput =
          card.querySelector('[data-de-addon-quantity]');

        const minusButton =
          card.querySelector('[data-de-addon-quantity-minus]');

        const plusButton =
          card.querySelector('[data-de-addon-quantity-plus]');

        checkbox?.addEventListener('change', () => {
          card.classList.toggle(
            'is-selected',
            checkbox.checked
          );

          this.setQuantityEnabled(
            card,
            checkbox.checked
          );

          this.updateTotals();
          this.updateCheckoutButton();
        });

        select?.addEventListener('change', () => {
          this.onAddonVariantChange(card, select);
        });

        minusButton?.addEventListener('click', () => {
          this.changeAddonQuantity(card, -1);
        });

        plusButton?.addEventListener('click', () => {
          this.changeAddonQuantity(card, 1);
        });

        quantityInput?.addEventListener('change', () => {
          this.normalizeAddonQuantity(card);
          this.updateTotals();
        });

        quantityInput?.addEventListener('input', () => {
          this.updateTotals();
        });

        this.setQuantityEnabled(
          card,
          Boolean(checkbox?.checked)
        );
      });
    }

    setQuantityEnabled(card, enabled) {
      const wrapper =
        card.querySelector('[data-de-addon-quantity-wrap]');

      const input =
        card.querySelector('[data-de-addon-quantity]');

      const minus =
        card.querySelector('[data-de-addon-quantity-minus]');

      const plus =
        card.querySelector('[data-de-addon-quantity-plus]');

      wrapper?.classList.toggle(
        'is-disabled',
        !enabled
      );

      if (input) input.disabled = !enabled;
      if (minus) minus.disabled = !enabled;
      if (plus) plus.disabled = !enabled;
    }

    getAddonQuantity(card) {
      const input =
        card.querySelector('[data-de-addon-quantity]');

      const max =
        Number(card.dataset.maxQuantity || 999999);

      let quantity =
        Number(input?.value || 1);

      if (!Number.isFinite(quantity) || quantity < 1) {
        quantity = 1;
      }

      quantity = Math.floor(quantity);

      if (max > 0) {
        quantity = Math.min(quantity, max);
      }

      return quantity;
    }

    normalizeAddonQuantity(card) {
      const input =
        card.querySelector('[data-de-addon-quantity]');

      if (!input) return;

      input.value =
        String(this.getAddonQuantity(card));
    }

    changeAddonQuantity(card, amount) {
      const input =
        card.querySelector('[data-de-addon-quantity]');

      if (!input || input.disabled) return;

      const current =
        this.getAddonQuantity(card);

      const max =
        Number(card.dataset.maxQuantity || 999999);

      let next = current + amount;

      next = Math.max(1, next);

      if (max > 0) {
        next = Math.min(next, max);
      }

      input.value = String(next);

      this.updateTotals();
    }

    createBundleCheckoutButton() {
      this.form
        .querySelectorAll(
          '[data-de-bundle-checkout], .de-addons__checkout-btn'
        )
        .forEach((button) => button.remove());

      this.hideOriginalDynamicCheckout();

      const button = document.createElement('button');

      button.type = 'button';

      button.className =
        'de-addons__checkout-btn btn btn-primary btn-large w-100';

      button.dataset.deBundleCheckout = 'true';
      button.textContent = 'PROCEED TO CHECKOUT';

      button.hidden = true;
      button.disabled = true;
      button.setAttribute('aria-hidden', 'true');

      button.style.setProperty(
        'display',
        'none',
        'important'
      );

      button.style.setProperty(
        'align-items',
        'center'
      );

      button.style.setProperty(
        'justify-content',
        'center'
      );

      button.addEventListener('click', () => {
        this.onBundleCheckout();
      });

      if (this.dynamicCheckoutWrapper) {
        this.dynamicCheckoutWrapper.insertAdjacentElement(
          'afterend',
          button
        );
      } else {
        const buttonsContainer =
          this.form.querySelector(
            '.product-form__buttons'
          );

        buttonsContainer?.appendChild(button);
      }

      this.bundleCheckoutButton = button;
    }

    hideOriginalDynamicCheckout() {
      const hide = () => {
        this.form
          ?.querySelectorAll(
            'sht-load-dyn-co-btn, .shopify-payment-button'
          )
          .forEach((element) => {
            element.hidden = true;

            element.setAttribute(
              'aria-hidden',
              'true'
            );

            element.style.setProperty(
              'display',
              'none',
              'important'
            );
          });
      };

      hide();

      window.setTimeout(hide, 500);
      window.setTimeout(hide, 1500);
    }

    readJson(selector) {
      try {
        return JSON.parse(
          this.querySelector(selector)?.textContent || '[]'
        );
      } catch (error) {
        console.error(
          '[ALLPOWERS Add-ons] JSON parse error:',
          error
        );

        return [];
      }
    }

    getCurrentMainVariant() {
      const id = Number(
        this.mainVariantInput?.value || 0
      );

      return (
        this.mainVariants.find(
          (variant) => Number(variant.id) === id
        ) ||
        this.mainVariants[0] ||
        null
      );
    }

    isCurrentMainPreorder() {
      const section =
        document.getElementById(
          this.dataset.sectionId
        );

      const selectedCard =
        section?.querySelector(
          '[data-de-option-card].is-selected'
        );

      return (
        selectedCard?.dataset.preorderEnabled === 'true' ||
        section?.classList.contains('ap-uk-po-special')
      );
    }

    getMainQuantity() {
      const quantity = Number(
        this.mainQuantityInput?.value || 1
      );

      return Number.isFinite(quantity) && quantity > 0
        ? Math.floor(quantity)
        : 1;
    }

    getSelectedAddons() {
      return this.cards
        .map((card) => {
          const checkbox =
            card.querySelector(
              '[data-de-addon-checkbox]'
            );

          if (
            !checkbox?.checked ||
            checkbox.disabled ||
            card.dataset.available !== 'true'
          ) {
            return null;
          }

          return {
            id: Number(card.dataset.variantId),
            quantity: this.getAddonQuantity(card),
            price: Number(card.dataset.price || 0)
          };
        })
        .filter(Boolean);
    }

    formatMoney(minorUnits) {
      const formatter = new Intl.NumberFormat(
        this.locale,
        {
          style: 'currency',
          currency: this.currency
        }
      );

      const digits =
        formatter.resolvedOptions()
          .maximumFractionDigits;

      return formatter.format(
        Number(minorUnits || 0) /
          Math.pow(10, digits)
      );
    }

    updateTotals() {
      const mainVariant =
        this.getCurrentMainVariant();

      const mainQuantity =
        this.getMainQuantity();

      const addons =
        this.getSelectedAddons();

      const mainTotal =
        Number(mainVariant?.price || 0) *
        mainQuantity;

      const extrasTotal =
        addons.reduce(
          (sum, item) =>
            sum +
            item.price *
            item.quantity,
          0
        );

      const mainNode =
        this.querySelector(
          '[data-de-addon-main-total]'
        );

      const extrasNode =
        this.querySelector(
          '[data-de-addon-extras-total]'
        );

      const grandNode =
        this.querySelector(
          '[data-de-addon-grand-total]'
        );

      if (mainNode) {
        mainNode.textContent =
          this.formatMoney(mainTotal);
      }

      if (extrasNode) {
        extrasNode.textContent =
          this.formatMoney(extrasTotal);
      }

      if (grandNode) {
        grandNode.textContent =
          this.formatMoney(
            mainTotal + extrasTotal
          );
      }
    }

    updateCheckoutButton() {
      const hasSelected =
        this.getSelectedAddons().length > 0;

      const isPreorder =
        this.isCurrentMainPreorder();

      this.hideOriginalDynamicCheckout();

      if (this.bundleCheckoutButton) {
        this.bundleCheckoutButton.hidden = isPreorder;
        this.bundleCheckoutButton.disabled = isPreorder;

        this.bundleCheckoutButton.setAttribute(
          'aria-hidden',
          String(isPreorder)
        );

        this.bundleCheckoutButton.style.setProperty(
          'display',
          isPreorder ? 'none' : 'flex',
          'important'
        );
      }

      this.purchaseNote?.classList.toggle(
        'is-hidden',
        !hasSelected
      );
    }

    onAddonVariantChange(card, select) {
      const option =
        select.options[select.selectedIndex];

      if (!option) return;

      const available =
        option.dataset.available === 'true';

      const price =
        Number(option.dataset.price || 0);

      const comparePrice =
        Number(option.dataset.comparePrice || 0);

      const variantId =
        Number(option.value);

      const maxQuantity =
        Number(option.dataset.maxQuantity || 999999);

      const checkbox =
        card.querySelector(
          '[data-de-addon-checkbox]'
        );

      const image =
        card.querySelector(
          '.de-addons__image'
        );

      const priceNode =
        card.querySelector(
          '[data-de-addon-price]'
        );

      const compareNode =
        card.querySelector(
          '[data-de-addon-compare]'
        );

      const discountNode =
        card.querySelector(
          '[data-de-addon-discount]'
        );

      const quantityInput =
        card.querySelector(
          '[data-de-addon-quantity]'
        );

      card.dataset.variantId =
        String(variantId);

      card.dataset.price =
        String(price);

      card.dataset.comparePrice =
        String(comparePrice);

      card.dataset.available =
        String(available);

      card.dataset.maxQuantity =
        String(maxQuantity);

      if (checkbox) {
        checkbox.value =
          String(variantId);

        checkbox.disabled =
          !available;

        if (!available) {
          checkbox.checked = false;
        }
      }

      if (quantityInput) {
        quantityInput.max =
          String(maxQuantity);

        this.normalizeAddonQuantity(card);
      }

      card.classList.toggle(
        'is-selected',
        Boolean(checkbox?.checked)
      );

      this.setQuantityEnabled(
        card,
        Boolean(
          available &&
          checkbox?.checked
        )
      );

      if (priceNode) {
        priceNode.textContent =
          this.formatMoney(price);
      }

      if (compareNode) {
        compareNode.textContent =
          comparePrice > price
            ? this.formatMoney(comparePrice)
            : '';

        compareNode.classList.toggle(
          'is-hidden',
          !(comparePrice > price)
        );
      }

      if (discountNode) {
        const percent =
          comparePrice > price
            ? Math.round(
                ((comparePrice - price) * 100) /
                comparePrice
              )
            : 0;

        discountNode.textContent =
          percent
            ? `−${percent}%`
            : '';

        discountNode.classList.toggle(
          'is-hidden',
          !percent
        );
      }

      if (
        image &&
        option.dataset.image
      ) {
        image.src =
          option.dataset.image;
      }

      this.updateTotals();
      this.updateCheckoutButton();
    }

    async onFormSubmit(event) {
      const addons =
        this.getSelectedAddons();

      if (!addons.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      await this.submitBundle({
        redirectToCheckout: false
      });
    }

    async onBundleCheckout() {
      if (this.isCurrentMainPreorder()) return;

      await this.submitBundle({
        redirectToCheckout: true
      });
    }

    async submitBundle({
      redirectToCheckout
    }) {
      if (this.isSubmitting) return;

      this.isSubmitting = true;
      this.hideError();
      this.setLoading(true);

      try {
        const mainItem =
          this.buildMainItem();

        if (!mainItem.id) {
          throw new Error(
            'Please select an available product variant first.'
          );
        }

        const addons =
          this.getSelectedAddons();

        const mergedItems =
          this.mergeItems([
            mainItem,
            ...addons
          ]);

        const sections =
          redirectToCheckout
            ? []
            : this.getSectionsToRender();

        const root =
          window.Shopify?.routes?.root ||
          '/';

        const addUrl =
          window.routes?.cart_add_url ||
          `${root}cart/add.js`;

        const payload = {
          items: mergedItems
        };

        if (sections.length) {
          payload.sections =
            sections;

          payload.sections_url =
            window.location.pathname;
        }

        const response =
          await fetch(addUrl, {
            method: 'POST',
            headers: {
              'Content-Type':
                'application/json',
              Accept:
                'application/json'
            },
            body:
              JSON.stringify(payload)
          });

        const data =
          await response.json();

        if (
          !response.ok ||
          data.status
        ) {
          throw new Error(
            data.description ||
            data.message ||
            'The products could not be added to your basket.'
          );
        }

        if (redirectToCheckout) {
          window.location.assign(
            `${root}checkout`
          );

          return;
        }

        this.renderCart(data);
      } catch (error) {
        console.error(
          '[ALLPOWERS Add-ons]',
          error
        );

        this.showError(
          error.message ||
          'The products could not be added to your basket.'
        );
      } finally {
        this.isSubmitting = false;
        this.setLoading(false);
      }
    }

    buildMainItem() {
      const formData =
        new FormData(this.form);

      const item = {
        id: Number(
          this.mainVariantInput?.value ||
          formData.get('id') ||
          0
        ),
        quantity:
          this.getMainQuantity()
      };

      const properties = {};

      for (
        const [key, value]
        of formData.entries()
      ) {
        const match =
          key.match(
            /^properties\[(.+)]$/
          );

        if (
          match &&
          value !== ''
        ) {
          properties[match[1]] =
            value;
        }
      }

      if (
        Object.keys(properties).length
      ) {
        item.properties =
          properties;
      }

      const sellingPlan =
        formData.get('selling_plan');

      if (sellingPlan) {
        item.selling_plan =
          Number(sellingPlan);
      }

      return item;
    }

    mergeItems(items) {
      const map = new Map();

      items.forEach((item) => {
        const key =
          `${item.id}:` +
          `${JSON.stringify(
            item.properties || {}
          )}:` +
          `${item.selling_plan || ''}`;

        if (map.has(key)) {
          map.get(key).quantity +=
            Number(item.quantity || 1);
        } else {
          map.set(key, {
            ...item,
            quantity:
              Number(item.quantity || 1)
          });
        }
      });

      return Array.from(map.values());
    }

    getSectionsToRender() {
      const ids = new Set([
        'cart-drawer',
        'header-cart-status'
      ]);

      const notification =
        document.querySelector(
          'sht-cart-noti'
        );

      const drawer =
        document.querySelector(
          'sht-cart-drwr'
        );

      notification
        ?.getSectionsToRender?.()
        .forEach((item) => {
          ids.add(item.id || item.section);
        });

      drawer
        ?.getSectionsToRender?.()
        .forEach((item) => {
          ids.add(item.id || item.section);
        });

      return Array.from(ids)
        .filter(Boolean);
    }

    renderCart(data) {
      const drawer =
        document.querySelector(
          'sht-cart-drwr'
        );

      const drawerForm =
        document.querySelector(
          'sht-cart-drwr-frm'
        );

      const headerCart =
        document.querySelector(
          '#headerCartStatus'
        );

      if (
        drawerForm?.renderContents &&
        data.sections
      ) {
        drawerForm.renderContents(data);
      }

      const headerHtml =
        data.sections?.[
          'header-cart-status'
        ];

      if (
        headerCart &&
        headerHtml
      ) {
        const doc =
          new DOMParser()
            .parseFromString(
              headerHtml,
              'text/html'
            );

        const section =
          doc.querySelector(
            '.shopify-section'
          ) ||
          doc.body;

        headerCart.innerHTML =
          section.innerHTML;
      }

      if (drawer?.openDrawer) {
        drawer.openDrawer(
          this.submitButton || this
        );
      } else {
        const root =
          window.Shopify?.routes?.root ||
          '/';

        window.location.href =
          window.routes?.cart_url ||
          `${root}cart`;
      }
    }

    setLoading(loading) {
      [
        this.submitButton,
        this.bundleCheckoutButton
      ].forEach((button) => {
        if (!button) return;

        button.classList.toggle(
          'loading',
          loading
        );

        button.toggleAttribute(
          'disabled',
          loading
        );

        button.setAttribute(
          'aria-disabled',
          String(loading)
        );
      });

      if (this.bundleCheckoutButton) {
        this.bundleCheckoutButton.textContent =
          loading
            ? 'LOADING…'
            : 'PROCEED TO CHECKOUT';
      }
    }

    showError(message) {
      if (!this.errorBox) return;

      this.errorBox.textContent =
        message;

      this.errorBox.classList.remove(
        'is-hidden'
      );
    }

    hideError() {
      if (!this.errorBox) return;

      this.errorBox.textContent = '';

      this.errorBox.classList.add(
        'is-hidden'
      );
    }
  }

  customElements.define(
    'de-product-add-ons',
    DeProductAddOns
  );
})();
